import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getUserByUsername } from '../firebase.js';
import { useAuth } from '../context/AuthContext.js';
import '../components/UserProfile.css';

const UserProfile = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    async function fetchUserData() {
      if (!username) {
        setError('No username provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        console.log(`🔍 Looking up profile for: ${username}`);
        
        // Lowercase the username for reliable lookup
        const normalizedUsername = username.toLowerCase();
        console.log(`🔄 Normalized username for lookup: ${normalizedUsername}`);
        
        // Use a timeout to prevent indefinite loading
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Request timed out')), 15000)
        );
        
        const userData = await Promise.race([
          getUserByUsername(username),
          timeoutPromise
        ]);
        
        if (!userData) {
          console.error(`❌ User not found for username: ${username}`);
          setError('User not found');
          setLoading(false);
          return;
        }
        
        console.log(`✅ User data found:`, userData);
        setUser(userData);
        
        // Check if current user is following this profile
        if (currentUser && userData.followers) {
          setIsFollowing(userData.followers.includes(currentUser.uid));
        }
      } catch (err) {
        console.error('❌ Error fetching user profile:', err);
        setError(err.message || 'Failed to load user profile');
      } finally {
        setLoading(false);
      }
    }

    fetchUserData();
  }, [username, currentUser, retryCount]);

  const handleFollow = async () => {
    if (!currentUser) {
      navigate('/login', { state: { from: `/u/profile/${username}` } });
      return;
    }
    
    // Toggle follow state immediately for better UX
    setIsFollowing(!isFollowing);
    
    try {
      // The actual follow/unfollow logic would be implemented here
      // This could call a Firebase function to update the followers collection
      console.log(`${isFollowing ? 'Unfollowing' : 'Following'} ${username}`);
    } catch (err) {
      // Revert the state if the operation fails
      setIsFollowing(!isFollowing);
      console.error('Error updating follow status:', err);
    }
  };

  const handleImageError = () => {
    setImgError(true);
  };

  const handleStartConversation = () => {
    if (!currentUser) {
      navigate('/login', { state: { from: `/u/profile/${username}` } });
      return;
    }
    
    // Navigate to messages with this user
    navigate(`/messages/new/${user.id}`);
  };

  // Function to retry loading the profile
  const retryLoading = () => {
    setLoading(true);
    setError(null);
    // Force re-render by updating the dependency
    setRetryCount(prev => prev + 1);
  };

  if (loading) {
    return (
      <div className="user-profile-container loading">
        <div className="profile-loader">
          <div className="loading-spinner"></div>
          <p>Loading profile...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="user-profile-container error">
        <div className="profile-error">
          <h3>Error Loading Profile</h3>
          <p>{error}</p>
          <div className="error-actions">
            <button onClick={retryLoading} className="retry-button">
              Retry
            </button>
            <button onClick={() => navigate(-1)} className="back-button">
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="user-profile-container not-found">
        <div className="profile-not-found">
          <h3>User Not Found</h3>
          <p>The user you&apos;re looking for doesn&apos;t exist or may have changed their username.</p>
          <div className="error-actions">
            <button onClick={retryLoading} className="retry-button">
              Retry
            </button>
            <button onClick={() => navigate(-1)} className="back-button">
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="user-profile-container">
      <div className="profile-header">
        <div className="profile-banner">
          {user.bannerURL && (
            <img 
              src={user.bannerURL} 
              alt={`${user.displayName || user.username}'s banner`} 
              className="banner-image"
            />
          )}
        </div>
        
        <div className="profile-info-wrapper">
          <div className="profile-avatar">
            <img 
              src={imgError ? 'https://firebasestorage.googleapis.com/v0/b/streamerwarz-b00b5.appspot.com/o/default-avatar.png?alt=media' : user.photoURL} 
              alt={`${user.displayName || user.username}'s avatar`} 
              onError={handleImageError}
            />
          </div>
          
          <div className="profile-info">
            <h1 className="profile-name">{user.displayName || user.username}</h1>
            <span className="profile-username">@{user.username}</span>
            
            {user.createdAt && (
              <span className="profile-joined">
                Joined {new Date(user.createdAt.toDate()).toLocaleDateString()}
              </span>
            )}
            
            {user.location && (
              <span className="profile-location">{user.location}</span>
            )}
          </div>
          
          <div className="profile-actions">
            {currentUser && currentUser.uid !== user.id && (
              <>
                <button 
                  onClick={handleFollow} 
                  className={`follow-button ${isFollowing ? 'following' : ''}`}
                >
                  {isFollowing ? 'Following' : 'Follow'}
                </button>
                
                <button 
                  onClick={handleStartConversation} 
                  className="message-button"
                >
                  Message
                </button>
              </>
            )}
            
            {currentUser && currentUser.uid === user.id && (
              <button 
                onClick={() => navigate('/settings/profile')} 
                className="edit-profile-button"
              >
                Edit Profile
              </button>
            )}
          </div>
        </div>
      </div>
      
      <div className="profile-content">
        <div className="profile-bio-section">
          {user.bio ? (
            <p className="profile-bio">{user.bio}</p>
          ) : (
            <p className="profile-bio empty">No bio provided</p>
          )}
        </div>
        
        <div className="profile-stats">
          <div className="stat-item">
            <span className="stat-value">{user.followers?.length || 0}</span>
            <span className="stat-label">Followers</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{user.following?.length || 0}</span>
            <span className="stat-label">Following</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{user.streamCount || 0}</span>
            <span className="stat-label">Streams</span>
          </div>
        </div>
        
        <div className="profile-tabs">
          <div className="tab active">Streams</div>
          <div className="tab">Highlights</div>
          <div className="tab">About</div>
        </div>
        
        <div className="profile-streams">
          {user.streams && user.streams.length > 0 ? (
            <div className="streams-grid">
              {user.streams.map(stream => (
                <div key={stream.id} className="stream-card">
                  <div className="stream-thumbnail">
                    <img src={stream.thumbnailURL} alt={stream.title} />
                    {stream.isLive && <span className="live-badge">LIVE</span>}
                  </div>
                  <div className="stream-info">
                    <h3 className="stream-title">{stream.title}</h3>
                    <p className="stream-viewers">{stream.viewCount} views</p>
                    <p className="stream-date">{new Date(stream.startedAt.toDate()).toLocaleDateString()}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-streams">
              <p>No streams yet</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserProfile; 